// === Route Components (base-routes.ts) ===
// Not wrapped with lazyLoad() here — wrapRoutesWithLazyLoad(baseRoutes) in base-providers.ts covers all routes.
const _dscLazyRoutes = {
    Startup: () => import('./startup/startup.component').then((m) => m.StartupComponent),
    Shell: () => import('./shell/shell.component').then((m) => m.ShellComponent),
    Dashboard: () => import('./dashboard/dashboard.component').then((m) => m.DashboardComponent),
    GeneralError: () => import('./general-error/general-error.component').then((m) => m.GeneralErrorComponent),
    AppUpdateInfo: () => import('./app-update-info/app-update-info.component').then((m) => m.AppUpdateInfoComponent),
    EmailLinkRoute: () => import('./email-link-route/email-link-route.component').then((m) => m.EmailLinkRouteComponent),
    NewHealthRecordLanding: () =>
        import('./health-record/external-landing/external-health-record-landing.component').then(
            (m) => m.ExternalHealthRecordLandingComponent,
        ),
    UnregisteredDataObjectionLanding: () =>
        import('./health-record/objection/unregistered-data-objection-landing.component').then(
            (m) => m.UnregisteredDataObjectionLandingComponent,
        ),
    DocumentRouteWrapper: () =>
        import('./document-package/document-viewer/document-viewer-wrapper/document-viewer-wrapper.component').then(
            (m) => m.DocumentRouteWrapperComponent,
        ),

    // === Sign-in routes ===
    LoginPage: () => import('./account/login-page/login-page.component').then((m) => m.LoginPageComponent),
    SignUpRegistrationPage: () =>
        import('./account/sign-up/sign-up-registration-page/sign-up-registration-page.component').then(
            (m) => m.SignUpRegistrationPageComponent,
        ),
    SignUpInvitationPage: () =>
        import('./account/sign-up/sign-up-invitation-page/sign-up-invitation-page.component').then((m) => m.SignUpInvitationPageComponent),
    SignUpSuccessPage: () =>
        import('./account/sign-up/sign-up-success-page/sign-up-success-page.component').then((m) => m.SignUpSuccessPageComponent),
    ResetPassword: () => import('./account/reset-password-page/reset-password-page.component').then((m) => m.ResetPasswordComponent),
    UpdateEmailVerification: () =>
        import('./account/update-email-page/update-email-verification-page.component').then((m) => m.UpdateEmailVerificationPageComponent),
    TermsOfUse: () => import('./terms-of-use/terms-of-use-page.component').then((m) => m.TermsOfUsePageComponent),
    SetNewPassword: () =>
        import('./account/set-new-password-page/set-new-password-page.component').then((m) => m.SetNewPasswordPageComponent),
    DeleteInvitation: () =>
        import('./account/delete-invitation/delete-invitation-page.component').then((m) => m.DeleteInvitationPageComponent),
    SelfCheckInRegistrationStart: () =>
        import('./self-check-in/self-check-in-registration-start/self-check-in-registration-start.component').then(
            (m) => m.SelfCheckInRegistrationStartComponent,
        ),
    SelfCheckInRegistrationSuccess: () =>
        import('./self-check-in/self-check-in-registration-success/self-check-in-registration-success.component').then(
            (m) => m.SelfCheckInRegistrationSuccessComponent,
        ),
    AccountFinishDeletion: () =>
        import('./account/account-finish-deletion/account-finish-deletion.component').then((m) => m.AccountFinishDeletionComponent),
    AppointmentPageExternal: () =>
        import('./appointment/appointment-process-external/appointment-page-external.component').then(
            (m) => m.AppointmentPageExternalComponent,
        ),
    AppointmentRegistrationSuccess: () =>
        import('./appointment/appointment-registration-success/appointment-registration-success.component').then(
            (m) => m.AppointmentRegistrationSuccessComponent,
        ),
    VeterinarianLinkageRequestRegistrationStart: () =>
        import('./veterinarian/veterinarian-linkage-request/veterinarian-linkage-request-registration-start/veterinarian-linkage-request-registration-start.component').then(
            (m) => m.VeterinarianLinkageRequestRegistrationStartComponent,
        ),

    // === Dashboard child routes ===
    AccountPage: () => import('./account/account-page/account-page.component').then((m) => m.AccountPageComponent),
    PatientsOverview: () => import('./patient/patients-overview/patients-overview.component').then((m) => m.PatientsOverviewComponent),
    AppointmentPage: () => import('./appointment/appointment-page/appointment-page.component').then((m) => m.AppointmentPageComponent),
    AssignmentOverview: () =>
        import('./assignment/assignment-overview/assignment-overview.component').then((m) => m.AssignmentOverviewComponent),
    DocumentPackagesOverview: () =>
        import('./document-package/document-packages-overview/document-packages-overview.component').then(
            (m) => m.DocumentPackagesOverviewComponent,
        ),
    EmergencyOverview: () =>
        import('./emergency/emergency-overview/emergency-overview.component').then((m) => m.EmergencyOverviewComponent),
    FinancesOverview: () => import('./finances/finances-overview/finances-overview.component').then((m) => m.FinancesOverviewComponent),
    GoodToKnowPage: () => import('./good-to-know/good-to-know-page/good-to-know-page.component').then((m) => m.GoodToKnowPageComponent),
    Help: () => import('./help/help.component').then((m) => m.HelpComponent),
    PatientDashboard: () => import('./patient/patient-dashboard/patient-dashboard.component').then((m) => m.PatientDashboardComponent),
    SelfCheckInProcessDialog: () =>
        import('./self-check-in/self-check-in-process-dialog/self-check-in-process-dialog.component').then(
            (m) => m.SelfCheckInProcessDialogComponent,
        ),
    UserDashboard: () => import('./user/user-dashboard/user-dashboard.component').then((m) => m.UserDashboardComponent),
    TimelineReduced: () => import('./timeline/timeline-reduced/timeline-reduced.component').then((m) => m.TimelineReducedComponent),
    VeterinarianOverview: () =>
        import('./veterinarian/veterinarian-overview/veterinarian-overview.component').then((m) => m.VeterinarianOverviewComponent),
    AccessibilityFeedbackForm: () =>
        import('./accessibility-feedback/form/accessibility-feedback-form.component').then((m) => m.AccessibilityFeedbackFormComponent),
};

export const dscLazyRoutes = _dscLazyRoutes;

// === Dialog Components (for services in @pxl/shared/services that cannot import from @pxl/dsc) ===
import { inject, Provider } from '@angular/core';
import { LAZY_DIALOG_REGISTRY, LazyDialogRegistry } from '@pxl/models/contracts';
import { DSC_LAZY_DIALOGS, DscLazyDialogService } from './services/dsc-lazy-dialog.service';

export function provideDscLazyDialogs(): Provider {
    return {
        provide: LAZY_DIALOG_REGISTRY,
        useFactory: (): LazyDialogRegistry => {
            const service = inject(DscLazyDialogService);
            return {
                PdfViewer: () => service.load(DSC_LAZY_DIALOGS.PdfViewer),
                MediaViewer: () => service.load(DSC_LAZY_DIALOGS.MediaViewer),
                AppointmentProcess: () => service.load(DSC_LAZY_DIALOGS.AppointmentProcess),
                MedicationAssignmentDetails: () => service.load(DSC_LAZY_DIALOGS.MedicationAssignmentDetails),
            };
        },
    };
}
